<template>
  <transition name="fade" mode="out-in">
    <div class="container" v-if="state.ready && !isPendingCampaign" :key="campaign.id">
      <template v-if="!state.onlyResults">
        <CampaignHeader></CampaignHeader>
        <Toast v-if="campaign.archived" :text="$t('app.campaign.single.archivedToast')" color="red" icon="bi-trash" style="max-width: unset" :closable="false" />
        <div class="filters-card">
          <div class="tabs">
            <div class="tab" :class="{ current: appStore.currentCampaignTab === 'Overview' }" @click="appStore.changeTab('Overview'); closePanelAndFilters()" v-text="$t('app.campaign.single.tab.summary')" />
            <div
              class="tab"
              :class="{
                current: appStore.currentCampaignTab === 'Tracking',
                hasNews: appStore.activatedTableHasNews[route.params.campaignId as string],
              }"
              data-step="8"
              @click="(appStore.changeTab('Tracking'), (appStore.activatedTableHasNews[route.params.campaignId as string] = false), closePanelAndFilters())"
              v-text="$t('app.campaign.single.tab.follow')"
            />
            <div
              class="tab"
              :class="{
                current: appStore.currentCampaignTab === 'Results',
              }"
              data-step="11"
              @click="appStore.changeTab('Results'); closePanelAndFilters()"
              v-text="$t('app.campaign.single.tab.results')"
            />
            <div
              v-if="canWriteCampaignAndSpaces(roles)"
              class="tab"
              :class="{
                current: appStore.currentCampaignTab === 'Parameters',
              }"
              @click="appStore.changeTab('Parameters'); closePanelAndFilters()"
              v-text="$t('app.campaign.single.tab.parameters')"
            />
          </div>
        </div>
      </template>
      <div v-if="isTalentPanelOpen" class="result-wrapper">
        <TalentTabs :openedTalents="openedTalents" @openedTalents="newOpenedTalents" @selectedTalent="selectedTalent"></TalentTabs>
        <TalentPanel :talent="state.selectedTalent" @closeTalentPanel="appStore.talentResultPanelIsOpen = false"></TalentPanel>
      </div>
      <ResultFilters v-else-if="state.resultFiltersIsOpen" :appliedFilters="state.resultFilters" @applyFilters="updateFilters" @closeFilters="state.resultFiltersIsOpen = false"></ResultFilters>
      <transition v-else mode="out-in" name="fade">
        <component :is="state.tabs[appStore.currentCampaignTab]" :search="state.search" :filters="state.resultFilters" @toggleFullscreen="state.onlyResults = !state.onlyResults" @openSelectedResults="openSelectedResults" @openResultFilters="state.resultFiltersIsOpen = true"/>
      </transition>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";
@import "@/assets/style/darkmode.scss";

  .result-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: var(--white-color);
    border: 1px solid var(--light-grey-color);
    border-radius: 8px;
    padding: 24px;
  }
</style>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { reactive, onMounted } from "vue";
import { useAppStore } from "@/stores/app.js";
import { useRoute } from "vue-router";

import Overview from "./partials/tables/Overview.vue";
import Tracking from "./partials/tables/Tracking.vue";
import Results from "./partials/tables/Results.vue";
import Parameters from "./partials/tables/Parameters.vue";
import TalentPanel from "./partials/results/TalentPanel.vue";
import TalentTabs from "./partials/results/TalentTabs.vue";
import ResultFilters from "./partials/results/ResultsFilters.vue";
import CampaignHeader from "@/components/campaigns/Header.vue";

import { useCampaign } from "@/queries/campaigns.query";
import { useRoles } from "@/queries/user.query.js";

import { canWriteCampaignAndSpaces } from "@/composables/permissions";
import { MemberRole } from "@/types/role.js";

const route = useRoute();

// Init stores
const appStore = useAppStore();
const { openedCandidateResults } = storeToRefs(appStore);

const { data: roles, isPending: isRolesPending } = useRoles();
const { data: campaign, isPending: isPendingCampaign } = useCampaign(appStore.onboardingIsLive);

const state = reactive({
  fullScreen: false,
  onlyResults: false,
  resultFiltersIsOpen: false,
  selectedTalent: undefined,
  resultFilters: undefined,
  search: {
    value: "",
    searchable: [
      {
        combined: true,
        keys: ["pending_first_name", "pending_last_name"],
      },
      "pending_email",
      {
        combined: true,
        keys: ["candidate.first_name", "candidate.last_name"],
      },
      "candidate.email",
    ],
  },
  tabs: markRaw({ Overview, Tracking, Results, Parameters }),
  paramsTab: appStore.campaignParametersTab,
  ready: true,
});

function updateFilters(filters) {
  state.resultFilters = filters;
}

function openSelectedResults(selectedResults) {
  appStore.openedCandidateResults = selectedResults;
  appStore.talentResultPanelIsOpen = true
}

function closePanelAndFilters() {
  appStore.talentResultPanelIsOpen = false;
  state.resultFiltersIsOpen = false;
}

function newOpenedTalents(talents) {
  appStore.openedCandidateResults = talents;

  if (appStore.openedCandidateResults.length == 0) {
    appStore.talentResultPanelIsOpen = false;
  }
}

function selectedTalent(selectedTalentParticipantId) {
  state.selectedTalent = openedCandidateResults.value.find((t) => t.participant_id == selectedTalentParticipantId);
}

const isTalentPanelOpen = computed(() => appStore.talentResultPanelIsOpen);
const openedTalents = computed(() => appStore.openedCandidateResults);

watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab && Object.keys(state.tabs).includes(newTab as string)) {
      appStore.currentCampaignTab = newTab as string;
    }
  }
)

onMounted(async () => {
  if (Object.keys(state.tabs).includes(route.query.tab as string)) {
    appStore.currentCampaignTab = route.query.tab as string;
  }
});

watch(roles, async () => {
  if (roles.value && roles.value.length > 0) {
    if (roles.value[0].role_backward_compatibility_id === MemberRole.CONSULTANT && !appStore.isOnboarded && appStore.onboardingPage != "campaign") {
      const campaignModule = await import("@/onboarding/campaign");
    appStore.startChapter(campaignModule.default);
    appStore.onboardingPage = "campaign";
    appStore.onboardingIsLive = true;
    }
  }
})
</script>
